import { useState } from "react";
import { HallSummary, ManagementApi } from "./api";
import { Locale } from "./copy";

interface AuditMetadata { audit_id: string; operation: string; recorded_at: string }
export function enrollmentAuditMetadata(value: unknown): AuditMetadata[] {
  if (!Array.isArray(value) || value.length > 100) throw new Error("Invalid audit response.");
  return value.map(row => {
    if (!row || typeof row.audit_id !== "string" || typeof row.operation !== "string"
      || typeof row.recorded_at !== "string" || !Number.isFinite(Date.parse(row.recorded_at))) {
      throw new Error("Invalid audit response.");
    }
    return { audit_id: row.audit_id, operation: row.operation, recorded_at: row.recorded_at };
  });
}

export function EnrollmentAudit({ api, hallId, locale }: { api: ManagementApi; hallId: string; locale: Locale }): React.JSX.Element {
  const es = locale === "es";
  const [result, setResult] = useState<{ status: string; rows: AuditMetadata[]; checked: string }>();
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const load = async (): Promise<void> => {
    setBusy(true); setError(""); setResult(undefined);
    try {
      const root = `/v1/halls/${encodeURIComponent(hallId)}`;
      const [hall, raw] = await Promise.all([api.get<HallSummary>(root), api.get<unknown>(`${root}/audit`)]);
      if (hall.hall_id !== hallId || typeof hall.status !== "string") throw new Error("Hall scope mismatch.");
      setResult({ status: hall.status, rows: enrollmentAuditMetadata(raw), checked: new Date().toISOString() });
    } catch {
      setError(es ? "No se pudo verificar la auditoría. No cambie la inscripción pendiente." : "Audit could not be verified. Do not change the pending enrollment.");
    } finally { setBusy(false); }
  };
  const signing = result?.rows.filter(row => row.operation === "pos.provisioning.issue") ?? [];
  return <details><summary>{es ? "Auditoría de inscripción" : "Enrollment audit"}</summary>
    <p>{es ? "Solo lectura. Muestra metadatos de la sala, no PIN ni archivos de inscripción." : "Read-only hall audit metadata. No PINs or enrollment exports are displayed."}</p>
    <button disabled={busy} onClick={() => void load()}>{es ? "Comprobar auditoría de inscripción" : "Check enrollment audit"}</button>
    {error && <p role="alert">{error}</p>}
    {result && <div role="status"><p>{hallId} · {result.status} · {result.checked}</p>
      <p>{es ? "Registros devueltos" : "Audit records returned"}: {result.rows.length} / 100. {es ? "Inscripciones POS firmadas en estos registros" : "Successful POS enrollment signings in these records"}: {signing.length}.</p>
      <p>{result.rows.length === 100
        ? (es ? "Límite alcanzado: los registros anteriores no están incluidos. La ausencia no demuestra que nunca se firmó." : "Limit reached: older records are not included. Absence does not prove no previous signing.")
        : (es ? "No se alcanzó el límite de la consulta." : "The audit query limit was not reached.")}</p>
      <p>{es ? "Los registros abarcan toda la sala, no identifican un terminal ni recuperan el archivo firmado. No cambie la secuencia automáticamente." : "These records cover the whole hall; they do not identify a terminal or recover its signed file. Do not change the sequence automatically."}</p>
      <ul>{result.rows.filter(row => row.operation === "pos.provisioning.issue" || row.operation === "hall.activate").map(row => <li key={row.audit_id}>{row.recorded_at} · {row.operation} · {row.audit_id}</li>)}</ul>
    </div>}
  </details>;
}
